/**
 * How the panel reaches the messaging API (#1069).
 *
 * One implementation, `ApiProvider`, talking to our own routes. There is no
 * selection to make: the messaging engine is first-party (PRD r9 resolved OQ1),
 * so nothing chooses between backends at runtime.
 *
 * ⚠️ THE NAME-SELECTION MACHINERY WAS REMOVED ON 2026-09-04, AND ITS ABSENCE IS
 * DELIBERATE. This module used to export `ProviderName`, `KNOWN_PROVIDER_NAMES`,
 * `isProviderName`, `readConfiguredProviderName` and `getProviderName`, and read
 * `REACT_APP_MESSAGING_PROVIDER` to pick an arm. With one arm, every branch of
 * that resolved to the same value: it was configuration surface for a choice
 * that no longer exists, and it told the next reader that a second backend was
 * planned. PRD NFR47a says the seam is the server's repository layer and that
 * nothing is layered on top of it to abstract a messaging product.
 *
 * ⚠️ WHAT SURVIVES, AND WHY. `IMessagingProvider` stays, because it earns its
 * keep for a reason unrelated to vendors: it lets the panel be tested against a
 * fake without standing up a server. That is a module boundary, not a
 * portability layer.
 *
 * The provider is still constructed lazily through `require`, so `apiProvider`
 * is not evaluated at *import* time outside a React Native runtime.
 */

import type { IMessagingProvider } from './provider.types';

export * from './provider.types';

let instance: IMessagingProvider | null = null;

/**
 * The messaging provider. One instance, reused.
 *
 * Memoised because it is cheap and because the test seam below wants one
 * thing to reset; nothing depends on there being exactly one. The provider
 * holds no identity (Story 2.3, D8: `connect`/`disconnect` went, who is
 * calling is the server's session), so a second instance would be harmless.
 */
export const getMessagingProvider = (): IMessagingProvider => {
  if (instance) return instance;
  const { ApiProvider } = require('./apiProvider');
  instance = new ApiProvider() as IMessagingProvider;
  return instance;
};

/** Test seam. Drops the memoised instance so the next call builds a fresh one. */
export const __resetMessagingProviderCacheForTests = (): void => {
  instance = null;
};
